/** @jsxImportSource @emotion/react */
'use client'

import { css } from '@emotion/react'
import CheckIcon from '@mui/icons-material/Check'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import Link from '@mui/material/Link'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import type React from 'react'
import { colorTokens } from '../../../styles/tokens'
import { FlexCol, FlexRow } from '../../layout'
import { styles } from './styles'

// Custom checkbox icons so the box border matches the 1px slate200 input border
// (MUI's default checkbox icon reads heavier than the outlined fields).
const checkboxStyles = {
  base: css`
    box-sizing: border-box;
    width: 18px;
    height: 18px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
  `,
  unchecked: css`
    border: 1px solid ${colorTokens.slate200};
    background: ${colorTokens.white};
  `,
  checked: css`
    border: 1px solid ${colorTokens.green100};
    background: ${colorTokens.green100};
    color: ${colorTokens.white};
  `,
  checkMark: css`
    font-size: 16px;
  `,
}

export interface LoginFormProps {
  email: string
  password: string
  onEmailChange: (value: string) => void
  onPasswordChange: (value: string) => void
  rememberMe: boolean
  onRememberMeChange: (value: boolean) => void
  onSubmit: () => void
  onForgotPassword: () => void
  error: string | null
  isLoading: boolean
}

export default function LoginForm({
  email,
  password,
  onEmailChange,
  onPasswordChange,
  rememberMe,
  onRememberMeChange,
  onSubmit,
  onForgotPassword,
  error,
  isLoading,
}: LoginFormProps) {
  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    onSubmit()
  }

  return (
    <form onSubmit={handleSubmit} noValidate>
      <FlexCol gap="lg">
        <FlexCol gap="xs">
          <Typography
            variant="h1"
            component="h1"
            css={[styles.headingFont, styles.heading]}
          >
            Welcome back
          </Typography>
          <Typography variant="body1" css={styles.lede}>
            Enter your email and password to access your account.
          </Typography>
        </FlexCol>

        <FlexCol gap="md">
          <div css={styles.field}>
            <Typography component="label" htmlFor="username" css={styles.label}>
              Email
            </Typography>
            <TextField
              id="username"
              hiddenLabel
              placeholder="name@company.co.uk"
              type="email"
              autoComplete="email"
              fullWidth
              variant="outlined"
              value={email}
              onChange={(event) => onEmailChange(event.target.value)}
            />
          </div>
          <div css={styles.field}>
            <Typography component="label" htmlFor="password" css={styles.label}>
              Password
            </Typography>
            <TextField
              id="password"
              hiddenLabel
              type="password"
              autoComplete="current-password"
              fullWidth
              variant="outlined"
              value={password}
              onChange={(event) => onPasswordChange(event.target.value)}
            />
          </div>

          <FlexRow justify="between" wrap>
            <FormControlLabel
              control={
                <Checkbox
                  checked={rememberMe}
                  onChange={(event) => onRememberMeChange(event.target.checked)}
                  icon={
                    <span
                      css={[checkboxStyles.base, checkboxStyles.unchecked]}
                    />
                  }
                  checkedIcon={
                    <span css={[checkboxStyles.base, checkboxStyles.checked]}>
                      <CheckIcon css={checkboxStyles.checkMark} />
                    </span>
                  }
                />
              }
              label={
                <Typography component="span" css={styles.supporting}>
                  Remember me
                </Typography>
              }
            />
            <Link
              component="button"
              type="button"
              underline="none"
              onClick={onForgotPassword}
              css={styles.inlineLink}
            >
              Forgot your password?
            </Link>
          </FlexRow>

          {error && (
            <Typography role="alert" color="error" variant="body2">
              {error}
            </Typography>
          )}

          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            disabled={isLoading}
            css={styles.primaryButton}
          >
            Sign in
          </Button>
        </FlexCol>
      </FlexCol>
    </form>
  )
}
